import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
  FetchApi,
  DiscoveryApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef, EntityRefLink } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Button,
} from '@material-ui/core';
import { Entity } from '@backstage/catalog-model';
import { useState } from 'react';

export interface GitHubCommit {
  sha: string;
  commit: {
    message: string;
    author: {
      name: string;
      email: string;
      date: string;
    };
  };
  author?: {
    login: string;
    avatar_url: string;
  };
}

export interface ErrorResponse {
  error: string;
}
interface TemplateWithCommitInfo extends Entity {
  commitInfo?: GitHubCommit;
}

const COLLAPSED_LIMIT = 3;
const EXPANDED_LIMIT = 8;
const COMMIT_MESSAGE_MAX_LENGTH = 100;

const truncateCommitMessage = (message: string): string => {
  if (message.length <= COMMIT_MESSAGE_MAX_LENGTH) {
    return message;
  }

  return `${message.slice(0, COMMIT_MESSAGE_MAX_LENGTH - 3)}...`;
};

const getCommitAuthor = (commit: GitHubCommit): string => {
  return commit.commit.author.name || commit.author?.login || 'Unknown author';
};

const extractRepoFromUrl = (
  url: string,
): { owner: string; repo: string } | null => {
  // example: url: "https://github.com/owner/repo/tree/main/packages"
  // { owner: "owner", repo: "repo" }
  try {
    const cleanUrl = url
      .replace(/^url:/, '')
      .replace(/^git\+/, '')
      .split('#')[0];

    const urlObj = new URL(cleanUrl);

    if (urlObj.hostname !== 'github.com') {
      return null;
    }

    const parts = urlObj.pathname.split('/').filter(Boolean);
    if (parts.length < 2) {
      return null;
    }

    return {
      owner: parts[0],
      repo: parts[1].replace(/\.git$/, ''),
    };
  } catch {
    // example: "https://github.com/just-owner"
    return null;
  }
};

const getLatestCommit = async (
  owner: string,
  repo: string,
  fetchApi: FetchApi,
  discoveryApi: DiscoveryApi,
): Promise<GitHubCommit | null> => {
  const baseUrl = await discoveryApi.getBaseUrl('github-commits');
  const response = await fetchApi.fetch(
    `${baseUrl}/commits/${owner}/${repo}?per_page=1`,
  );

  if (!response.ok) {
    return null;
  }

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return null;
  }

  const data: GitHubCommit[] = await response.json();
  const commit = data[0];
  if (!commit) {
    return null;
  }
  return commit;
};

export const RecentTemplateChanges = () => {
  const catalogApi = useApi(catalogApiRef);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [expanded, setExpanded] = useState(false);

  const { value, loading, error } = useAsync(async () => {
    const { items } = await catalogApi.getEntities({
      filter: { kind: 'Template' },
    });

    const templatesWithCommits = await Promise.all(
      items.map(async (template): Promise<TemplateWithCommitInfo> => {
        const sourceUrl =
          template.metadata.annotations?.['backstage.io/source-location'] ||
          (typeof template.spec?.repoUrl === 'string'
            ? template.spec.repoUrl
            : null);

        if (!sourceUrl) {
          return template;
        }

        const repoInfo = extractRepoFromUrl(sourceUrl);
        if (!repoInfo) {
          return template;
        }

        const commitInfo = await getLatestCommit(
          repoInfo.owner,
          repoInfo.repo,
          fetchApi,
          discoveryApi,
        );

        return {
          ...template,
          commitInfo: commitInfo || undefined,
        };
      }),
    );

    return templatesWithCommits.sort((a, b) => {
      const aDate = a.commitInfo?.commit?.author?.date
        ? new Date(a.commitInfo.commit.author.date).getTime()
        : 0;
      const bDate = b.commitInfo?.commit?.author?.date
        ? new Date(b.commitInfo.commit.author.date).getTime()
        : 0;
      return bDate - aDate;
    });
  }, [catalogApi, fetchApi, discoveryApi]);

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  const templatesWithCommits = (value || []).filter(
    template => template.commitInfo,
  );
  const visibleTemplates = templatesWithCommits.slice(
    0,
    expanded ? EXPANDED_LIMIT : COLLAPSED_LIMIT,
  );
  const hasMoreThanCollapsedLimit =
    templatesWithCommits.length > COLLAPSED_LIMIT;
  const toggleAction = hasMoreThanCollapsedLimit ? (
    <Button onClick={() => setExpanded(!expanded)}>
      {expanded ? 'VIEW LESS' : 'VIEW MORE'}
    </Button>
  ) : undefined;

  return (
    <InfoCard title="Accelerator Updates" actions={toggleAction}>
      {templatesWithCommits.length === 0 ? (
        <Box p={2}>
          <Typography variant="body2" color="textSecondary">
            No templates found
          </Typography>
        </Box>
      ) : (
        <>
          <List>
            {visibleTemplates.map(template => {
              const commitInfo = template.commitInfo!;
              const commitMessage = commitInfo.commit.message || 'No message';

              return (
                <ListItem key={template.metadata.uid}>
                  <ListItemText
                    primary={<EntityRefLink entityRef={template} />}
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={
                      <Box>
                        <Typography variant="caption" component="div">
                          {`${new Date(
                            commitInfo.commit.author.date,
                          ).toLocaleString()} • ${getCommitAuthor(commitInfo)}`}
                        </Typography>
                        <Typography
                          variant="caption"
                          component="div"
                          title={commitMessage}
                        >
                          {truncateCommitMessage(commitMessage)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </>
      )}
    </InfoCard>
  );
};
